/**
 * Server-evaluated feature flags for the signed-in tenant (GET /v1/flags/me, QM-C07-003).
 * Fail-closed: while loading, on any error, with no tenant, or for a key the server does not know, a flag is OFF.
 */
import { useApp } from './app-context';
import { useResource } from './resource';

/** KPI + attribution dashboard screens (/analytics). Off by default; staff enable it per tenant after review. */
export const KPI_DASHBOARD_FLAG = 'attribution.dashboard';

/**
 * Full Quixo OS for a workspace. Phase 1 (#137, 25 Oct 2026) hid every module unless this flag was on; that left every
 * workspace with Dashboard / Audit / Billing / Team / Help only, so the owner restored the full menu (28 Oct 2026).
 * It still switches on modules a workspace has marked audit-only, and AI search (whose API checks it server-side).
 */
export const PRODUCT_OS_FLAG = 'product.os';
/**
 * Opt-in Phase-1 view: a workspace with this flag on sees the audit-only menu and home (modules hidden unless
 * PRODUCT_OS_FLAG or the module's own flag is on). Unknown = off, so every workspace gets the full menu by default.
 */
export const AUDIT_ONLY_FLAG = 'product.audit_only';
/** Per-module switches (G10): they show a module in an audit-only workspace. */
export const MODULE_FLAGS = {
  crm: 'module.crm', campaigns: 'module.campaigns', delivery: 'module.delivery', visibility: 'module.visibility',
  whitelabel: 'module.whitelabel', autorag: 'knowledge.autorag', whatsapp: 'module.whatsapp', integrations: 'module.integrations',
} as const;
/** A module with its own switch, or 'os' for screens only PRODUCT_OS_FLAG brings back in an audit-only workspace. */
export type ModuleKey = keyof typeof MODULE_FLAGS | 'os';
/** Flags that show a module: the OS switch or the module's own. */
export const moduleFlags = (m: ModuleKey): readonly string[] => (m === 'os' ? [PRODUCT_OS_FLAG] : [PRODUCT_OS_FLAG, MODULE_FLAGS[m]]);
/** Modules whose API enforces the flag itself (auto-rag entitlement): fail-closed in the portal too. */
export const SERVER_GATED_MODULES: ReadonlySet<ModuleKey> = new Set(['autorag']);

/** 'show' | 'hide' | 'wait' for a module given the flag states (see AUDIT_ONLY_FLAG and SERVER_GATED_MODULES). */
export function moduleState(m: ModuleKey, flag: (key: string) => FlagState): 'show' | 'hide' | 'wait' {
  const states = moduleFlags(m).map(flag);
  if (states.includes('on')) return 'show';
  if (!SERVER_GATED_MODULES.has(m)) {
    const auditOnly = flag(AUDIT_ONLY_FLAG);
    if (auditOnly === 'off') return 'show';
    if (auditOnly === 'loading') return 'wait';
  }
  return states.every((s) => s === 'off') ? 'hide' : 'wait';
}

/** Full Quixo OS home and billing unless the workspace is audit-only (and the OS is not switched on for it). */
export function useFullOs(): boolean {
  const flag = useFlags();
  return !(flag(AUDIT_ONLY_FLAG) === 'on' && flag(PRODUCT_OS_FLAG) !== 'on');
}

/** Knowledge base website crawler + scanned-file OCR (Pending Build item 15). Off by default. */
export const KNOWLEDGE_FLAGS = ['knowledge.crawler', 'knowledge.ocr'] as const;
/** Nav condition: the tenant already has knowledge sources (the screen stays reachable with both flags off). */
export const KNOWLEDGE_HAS_SOURCES = 'knowledge.hasSources';

/**
 * Asks for the tenant's sources only when both knowledge flags are resolved and off (fail-closed: loading or error
 * means "no sources", so the entry stays hidden).
 */
export function useKnowledgeHasSources(flag: (key: string) => FlagState, enabled = true): boolean {
  const { me } = useApp();
  const need = enabled && !!me?.tenantId && KNOWLEDGE_FLAGS.every((k) => flag(k) === 'off');
  const res = useResource<unknown[]>(need ? '/v1/knowledge/sources' : null);
  return need && res.status === 'ready' && Array.isArray(res.data) && res.data.length > 0;
}

export type FlagState = 'loading' | 'on' | 'off';

export function flagState(res: { status: 'loading' | 'ready' | 'error'; data: { flags?: Record<string, boolean> } | null }, key: string): FlagState {
  if (res.status === 'loading') return 'loading';
  return res.status === 'ready' && res.data?.flags?.[key] === true ? 'on' : 'off';
}

/** All flags at once: `(key) => state`. One cached request per tenant session (cache purged on sign-out / switch). */
export function useFlags(enabled = true): (key: string) => FlagState {
  const { me } = useApp();
  const on = enabled && !!me?.tenantId;
  const res = useResource<{ flags: Record<string, boolean> }>(on ? '/v1/flags/me' : null);
  return (key) => (on ? flagState(res, key) : 'off');
}

export function useFlag(key: string): FlagState {
  return useFlags()(key);
}
